import type {
  AgentInput,
  AgentTool,
  AgentToolDetail,
  AgentToolDetailVendor,
  TaskCatalogEntry,
} from '@onefootprint/request-types/dashboard';
import { humanizeVendorKind, toVendorKind } from '@/utils/vendor-kind';
import { type InputField, isBuiltinTool, isMcpTool, isVendorTool } from '../../store/types';

type MissingTool = { id: string; name: string; tool: AgentTool; unavailableReason?: string };

type MissingInput = AgentInput & {
  declaredOptional?: boolean;
  /** Only a document input loads the file the entry reads. */
  declaredNotDocument?: boolean;
};

type CatalogRequirements = {
  inputs: MissingInput[];
  /** Inputs an earlier task already produces, so the new task reads its output instead. */
  fromEarlierTasks: string[];
  tools: MissingTool[];
};

type AgentState = {
  inputs: Pick<InputField, 'name' | 'required' | 'type'>[];
  producedKeys: Set<string>;
  tools: AgentTool[];
};

const toolId = (tool: AgentTool): string => {
  if (isVendorTool(tool)) return `vendor:${tool.data.vendorKind}`;
  if (isMcpTool(tool)) return `mcp:${tool.data.serverId}`;
  return `builtin:${tool.data.kind}`;
};

const describeTool = (tool: AgentTool, catalog: AgentToolDetail[]): MissingTool => {
  if (isMcpTool(tool)) return { id: tool.data.serverId, name: tool.data.serverId, tool };
  if (isBuiltinTool(tool)) {
    const { kind } = tool.data;
    const detail = catalog.find(entry => entry.toolKind === 'builtin' && entry.kind === kind);
    // Built-in tools need no tenant setup, so one is never unavailable.
    return { id: kind, name: detail?.displayName ?? kind, tool };
  }
  const { vendorKind } = tool.data;
  const detail = catalog.find(
    (entry): entry is AgentToolDetailVendor => entry.toolKind !== 'builtin' && toVendorKind(entry.kind) === vendorKind,
  );
  if (!detail)
    return { id: vendorKind, name: humanizeVendorKind(vendorKind), tool, unavailableReason: 'Not available' };
  return { id: vendorKind, name: detail.displayName, tool, unavailableReason: detail.disabledReason };
};

const missingRequirements = (
  entry: TaskCatalogEntry,
  agent: AgentState,
  toolCatalog: AgentToolDetail[],
): CatalogRequirements => {
  const declared = new Map(agent.inputs.map(input => [input.name, input]));
  const isProduced = (input: AgentInput) => agent.producedKeys.has(input.fieldName);
  const inputs = entry.inputs.flatMap((input): MissingInput[] => {
    if (isProduced(input)) return [];
    const existing = declared.get(input.fieldName);
    if (!existing) return [input];
    const declaredOptional = input.isRequired && !existing.required;
    const declaredNotDocument = input.schema?.format === 'document' && existing.type !== 'document';
    return declaredOptional || declaredNotDocument ? [{ ...input, declaredOptional, declaredNotDocument }] : [];
  });
  return {
    inputs,
    fromEarlierTasks: entry.inputs.filter(i => isProduced(i) && !declared.has(i.fieldName)).map(i => i.fieldName),
    tools: entry.requiredTools
      .filter(tool => !agent.tools.some(enabled => toolId(enabled) === toolId(tool)))
      .map(tool => describeTool(tool, toolCatalog)),
  };
};

const hasRequirements = ({ inputs, tools }: CatalogRequirements): boolean => inputs.length > 0 || tools.length > 0;

export type { AgentState, CatalogRequirements };
export { hasRequirements, missingRequirements };
